'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { useLocale, useTranslations } from 'next-intl'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Alert, Box, Button, Card, CardContent, CircularProgress, IconButton, Stack, Typography } from '@mui/material'

import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { useAccountTravellers } from '@/src/hooks/travellers/useAccountTravellers'
import { DetailItem } from '@/src/components/desktop/accounts/travellers/travellers'
import { TravellerDetailWrapper } from '@/src/components/desktop/accounts/travellers/travellerDetail'

import { MobileTravellersView } from './mobileTravellersView'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../../common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

const MobileTravellersPageWrapper: React.FC = () => {
    const t = useTranslations('account.travellers')
    const locale = useLocale()
    const goToHome = useRedirectToHome()
    const isRtl = locale === 'ar'
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn

    const [isMounted, setIsMounted] = useState(false)
    const [isSignInOpen, setIsSignInOpen] = useState(false)
    const [selectedTravellerId, setSelectedTravellerId] = useState<number | null>(null)
    const { isLoading, isError, refetch, travellersForView, activeTravellerForView, onSelectTraveller } = useAccountTravellers()

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMounted(true)
        }
    }, [])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
        if (!isLoggedIn) {
            goToHome({ isReloadRequired: false })
        }
    }, [isLoggedIn])

    const isDetailMode = selectedTravellerId != null

    const onOpenTraveller = useCallback(
        (id: number) => {
            onSelectTraveller(id)
            setSelectedTravellerId(id)
        },
        [onSelectTraveller]
    )

    const onBackToList = useCallback(() => {
        setSelectedTravellerId(null)
    }, [])
    const onBackToAccount = useCallback(() => {
        goToHome({ isReloadRequired: false })
    }, [])

    const detailTraveller = useMemo(() => {
        if (!isDetailMode) return null
        return activeTravellerForView
    }, [isDetailMode, activeTravellerForView])

    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {isError && !isLoading && (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert
                        severity="error"
                        sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}
                        action={
                            <Button color="inherit" size="small" onClick={refetch} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {t('retry')}
                            </Button>
                        }
                    >
                        {t('failedToLoad')}
                    </Alert>
                </Box>
            )}

            {!isLoading && !isError && !isDetailMode && (
                <MobileTravellersView
                    t={t}
                    travellers={travellersForView}
                    onSelectTraveller={onOpenTraveller}
                    onBack={onBackToAccount}
                    isRtl={isRtl}
                />
            )}

            {!isLoading && !isError && isDetailMode && detailTraveller && (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5, position: 'relative' }}
                    >
                        <IconButton aria-label={t('title')} onClick={onBackToList} sx={{ ml: -1 }}>
                            <ArrowBackIcon sx={{ fontSize: 20, transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '16px', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
                        >
                            {t('details')}
                        </Typography>
                        <Box sx={{ width: 40 }} />
                    </Stack>

                    <Box sx={{ px: 1.5, py: 2 }}>
                    <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', mb: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', lineHeight: '24px' }}>
                                        {detailTraveller.displayName}
                                    </Typography>

                                    <Stack direction="row" spacing={0.5}>
                                        <IconButton disabled aria-label={t('edit')} sx={{ width: 32, height: 32 }}>
                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <Box sx={{ width: '1px', height: '16px', bgcolor: 'divider', alignSelf: 'center' }} />
                                        <IconButton disabled aria-label={t('delete')} sx={{ width: 32, height: 32 }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                <DetailItem label={t('gender')} value={detailTraveller.gender} />
                                <DetailItem label={t('email')} value={detailTraveller.email} />
                                <DetailItem label={t('dateOfBirth')} value={detailTraveller.dateOfBirthValue} />
                                <DetailItem label={t('mobileNumber')} value={detailTraveller.phoneLabel} />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                        <CardContent sx={{ px: 1.5, py: 2 }}>
                            <TravellerDetailWrapper t={t} documents={detailTraveller.documents} />
                        </CardContent>
                    </Card>
                    </Box>
                </>
            )}

            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </Box>
    )
}

export { MobileTravellersPageWrapper }
export default MobileTravellersPageWrapper
